import type { PluginDataSourceType } from './plugin-types';
import { loadPluginModuleRaw, isRemoteAllowed } from './plugin-loader';

/**
 * 数据源类型注册表（阶段3）：收集已启用插件的 dataSourceTypes 声明。
 * 插件安装并启用后，其 dataSourceTypes 被注册到此处；禁用/卸载后移除。
 */
class DataSourceTypeRegistry {
  private registry = new Map<string, PluginDataSourceType>();
  private listeners = new Set<() => void>();

  register(types: PluginDataSourceType[], pluginId: string): void {
    for (const t of types) {
      if (this.registry.has(t.type)) {
        console.warn(`数据源类型 ${t.type} 已被注册，忽略插件 ${pluginId}`);
        continue;
      }
      this.registry.set(t.type, { ...t, __pluginId: pluginId } as PluginDataSourceType & { __pluginId: string });
    }
    this.notifyListeners();
  }

  unregister(types: PluginDataSourceType[]): void {
    for (const t of types) {
      this.registry.delete(t.type);
    }
    this.notifyListeners();
  }

  getAll(): PluginDataSourceType[] {
    return Array.from(this.registry.values());
  }

  get(type: string): PluginDataSourceType | undefined {
    return this.registry.get(type);
  }

  has(type: string): boolean {
    return this.registry.has(type);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  clear(): void {
    this.registry.clear();
    this.notifyListeners();
  }

  private notifyListeners(): void {
    this.listeners.forEach((l) => l());
  }
}

export const dataSourceTypeRegistry = new DataSourceTypeRegistry();

/** 从插件模块提取 dataSourceTypes 声明（支持 module.dataSourceTypes / module.default.dataSourceTypes） */
export function extractDataSourceTypes(module: unknown): PluginDataSourceType[] {
  const m = (module || {}) as any;
  const types = m?.dataSourceTypes ?? m?.default?.dataSourceTypes;
  return Array.isArray(types) ? (types as PluginDataSourceType[]) : [];
}

/** 加载已启用插件的 dataSourceTypes 并注册（幂等：同类型跳过） */
export async function registerPluginDataSourceTypes(plugin: { id: string; url?: string; entry?: string }): Promise<void> {
  if (!plugin.url || !isRemoteAllowed(plugin.url)) return;
  try {
    const module = await loadPluginModuleRaw(plugin.url);
    const types = extractDataSourceTypes(module);
    if (types.length > 0) {
      dataSourceTypeRegistry.register(types, plugin.id);
    }
  } catch (e) {
    console.warn(`加载插件 ${plugin.id} 的数据源类型失败`, e);
  }
}
